import {
  format,
  formatDistanceToNow,
  fromUnixTime,
  differenceInCalendarDays,
  addMonths,
  startOfMonth,
  startOfDay,
  endOfDay,
  addDays,
  getUnixTime,
  endOfMonth,
  parseISO,
  isBefore,
  isAfter,
  parse,
} from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime, getTimezoneOffset } from 'date-fns-tz';

import { clamp } from '/src/utils/math';
import { SPECIAL_DATE_RANGES } from '/src/features/_global/constants';

const { TODAY, LAST_7_DAYS, LAST_30_DAYS, LAST_100_DAYS, LAST_MONTH, THIS_MONTH } = SPECIAL_DATE_RANGES;

/**
 * Returns the start of the UTC day for the given input.
 */
export function toUtcStart(input) {
  if (!input) return null;

  const parsedInput = normalizeDate(input);

  const localStart = startOfDay(parsedInput);
  const utcStart = zonedTimeToUtc(localStart, 'UTC');

  if (typeof input === 'number') {
    return getUnixTime(utcStart);
  }
  return utcStart;
}

/**
 * Returns the end of the UTC day for the given input.
 */
export function toUtcEnd(input) {
  if (!input) return null;

  const parsedInput = normalizeDate(input);

  const localEnd = endOfDay(parsedInput);
  const utcEnd = zonedTimeToUtc(localEnd, 'UTC');

  if (typeof input === 'number') {
    return getUnixTime(utcEnd);
  }
  return utcEnd;
}

export function formatDate(input) {
  return format(fromUnixTime(input), `MMM dd, yyyy`).replace(/\./g, '');
}

export function formatFullDate(input) {
  return format(fromUnixTime(input), `MMMM dd, yyyy`).replace(/\./g, '');
}

export function formatMinimalDate(input) {
  return format(fromUnixTime(input), `MMMM d, yyyy`).replace(/\./g, '');
}

export function formatParseMinimalDate(input) {
  return format(parse(input, 'yyyy-MM-dd', new Date()), `MMMM d, yyyy`).replace(/\./g, '');
}

export function formatParseFullDate(input) {
  return format(parse(input, 'yyyy-MM-dd', new Date()), `MMMM dd, yyyy`).replace(/\./g, '');
}

export function formatParseFullDateOfWeek(input) {
  return format(parse(input, 'yyyy-MM-dd', new Date()), `MMMM dd, yyyy - EEEE`).replace(/\./g, '');
}

export function formatShortDate(input) {
  const date = typeof input === 'number' ? fromUnixTime(input) : parse(input, 'yyyy-MM-dd', new Date());
  return format(date, `MMM dd`).replace(/\./g, '');
}

export function formatShortFullDate(input) {
  return format(fromUnixTime(input), `MMMM dd`).replace(/\./g, '');
}

export function formatShortMinimalDate(input) {
  const date = typeof input === 'number' ? fromUnixTime(input) : parse(input, 'yyyy-MM-dd', new Date());
  return format(date, `MMMM d`).replace(/\./g, '');
}

export function formatLongDate(input) {
  return format(fromUnixTime(input), `EEEE do MMMM, yyyy`).replace(/\./g, '');
}

export function formatDateTime(input) {
  return format(fromUnixTime(input), `MMM dd, yyyy 'at' h:mmaaaa`).replace(/\./g, '');
}

export function formatShortDateTime(input) {
  return format(fromUnixTime(input), `MMM dd, h:mm aaaa`).replace(/\./g, '');
}

export function formatFullDateTime(input) {
  return format(fromUnixTime(input), `MMMM dd, y, h:mm aaaa`).replace(/\./g, '');
}

export function utcStringToLocalTimeObject(input) {
  return utcToZonedTime(parseISO(input));
}

export function utcStringToLocalTimeFullDateTime(input) {
  return format(utcToZonedTime(parseISO(input)), `MMMM dd, y, h:mm aaaa`).replace(/\./g, '');
}

export function utcStringToLocalTimeDate(input) {
  return format(utcToZonedTime(parseISO(input)), `MMM dd, y`).replace(/\./g, '');
}

export function formatTime(input) {
  return format(fromUnixTime(input), `h:mm aaa`).replace(/\./g, '');
}

export function formatParseDayOfWeek(input) {
  return format(parse(input, 'yyyy-MM-dd', new Date()), 'EEEE');
}

function getDateInServerLocalTime(input) {
  const serverTimeZone = 'Etc/UTC';
  const utcTime = zonedTimeToUtc(
    format(input, 'yyyy-MM-dd HH:mm'),
    Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
  );
  return utcToZonedTime(utcTime, serverTimeZone);
}

export function normalizeDate(input) {
  if (input instanceof Date) return input;
  if (typeof input === 'number') {
    // if it is seconds, convert from unix timestamp
    // if it is milliseconds, convert directly to Date
    return input < 1e12 ? fromUnixTime(input) : new Date(input);
  }
  // try parse whatever is provided
  return new Date(input);
}

export function formattedCurrentDate() {
  return new Date()
    .toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })
    .replaceAll('/', '-');
}

export function getDaysRemainingInMonthServerTime() {
  const serverTime = getDateInServerLocalTime(new Date());
  return clamp(
    differenceInCalendarDays(getDateInServerLocalTime(addMonths(startOfMonth(serverTime), 1)), serverTime),
    0,
    31,
  );
}

export function getDaysRemainingInMonthServerTimeFloatType() {
  const serverTime = getDateInServerLocalTime(new Date());
  const remainingDays = parseInt(getDaysRemainingInMonthServerTime());
  const serverHours = parseInt(format(serverTime, 'k'));

  if (serverHours >= 1 && serverHours < 12) {
    return remainingDays;
  } else if (serverHours >= 12 && serverHours < 24) {
    return remainingDays - 0.5;
  } else {
    return remainingDays - 1;
  }
}

function padNumber(value, padded = false) {
  if (!padded) return value;

  if (value >= 10) {
    return value;
  }
  return `0${value}`;
}

export function msToHHMMSS(ms, padded = false) {
  let seconds = Math.floor(ms / 1000);
  const hours = parseInt(seconds / 3600);
  seconds = seconds % 3600;
  const minutes = parseInt(seconds / 60);
  seconds = seconds % 60;

  if (hours > 0) {
    return `${padNumber(hours, padded)}:${padNumber(minutes, true)}:${padNumber(seconds, true)}`;
  }
  return `${padNumber(minutes, padded)}:${padNumber(seconds, true)}`;
}

export function msToHumanDuration(ms, compact = false) {
  let totalSeconds = ms < 1000 ? Math.round(ms / 10) / 100 : Math.floor(ms / 1000);

  let days = Math.floor(totalSeconds / 86400);
  totalSeconds -= days * 86400;

  let hours = Math.floor(totalSeconds / 3600);
  totalSeconds -= hours * 3600;

  let minutes = Math.floor(totalSeconds / 60);
  let seconds = totalSeconds - minutes * 60;

  if (ms < 1000) {
    seconds = Number(seconds.toFixed(2));
    if (seconds >= 60) {
      minutes += 1;
      seconds = 0;
    }
  } else {
    seconds = Math.floor(seconds);
  }

  if (minutes >= 60) {
    hours += Math.floor(minutes / 60);
    minutes = minutes % 60;
  }

  if (hours >= 24) {
    days += Math.floor(hours / 24);
    hours = hours % 24;
  }

  const formatSeconds = () => {
    if (ms >= 1000) {
      return `${seconds}s`;
    }
    const fixed = seconds % 1 === 0 ? seconds.toFixed(0) : seconds.toFixed(2);
    return `${fixed}s`;
  };

  let returnValue = '';
  if (days > 0) {
    returnValue += ` ${days}d`;
  }
  if (hours > 0) {
    returnValue += ` ${hours}h`;
  }
  if (minutes > 0) {
    returnValue += ` ${minutes}m`;
  }
  if (compact && seconds > 0) {
    returnValue += ` ${formatSeconds()}`;
  } else if (!compact) {
    returnValue += ` ${formatSeconds()}`;
  }

  if (returnValue === '') {
    returnValue += '0s';
  }

  return returnValue.trim();
}

export function formatDuration(input, { units = 'ms', padded = false } = {}) {
  if (!['ms', 's'].includes(units)) {
    throw new Error('formatDuration only accepts seconds (s) and milliseconds (ms)');
  }
  const date = new Date(units === 'ms' ? input : input * 1000);
  return format(date, padded ? 'mm:ss' : 'm:ss');
}

export const TIMEZONE_OFFSET_SECONDS = new Date().getTimezoneOffset() * 60;

export const calcZonedDate = (date, fn, tz = 'Etc/UTC') => {
  const inputZoned = utcToZonedTime(date, tz);
  const fnZoned = fn(inputZoned);
  return zonedTimeToUtc(fnZoned, tz);
};

export const getZonedEndOfDay = (date, timeZone) => {
  return calcZonedDate(date, endOfDay, timeZone);
};

export const getZonedStartOfDay = (date, timeZone) => {
  return calcZonedDate(date, startOfDay, timeZone);
};

export const getZonedEndOfMonth = (date, timeZone) => {
  return calcZonedDate(date, endOfMonth, timeZone);
};

export const getZonedStartOfMonth = (date, timeZone) => {
  return calcZonedDate(date, startOfMonth, timeZone);
};

export function getConvertedDateRange(daterange, toUTC = false, relativeTo = new Date()) {
  let startDate, endDate;

  const now = new Date();
  switch (daterange.special) {
    case TODAY:
      startDate = toUTC ? getZonedStartOfDay(now) : startOfDay(now);
      endDate = toUTC ? getZonedEndOfDay(now) : endOfDay(now);
      break;
    case LAST_7_DAYS:
      startDate = toUTC ? getZonedStartOfDay(addDays(relativeTo, -7)) : startOfDay(addDays(relativeTo, -7));
      endDate = toUTC ? getZonedEndOfDay(relativeTo) : endOfDay(relativeTo);
      break;
    case LAST_30_DAYS:
      startDate = toUTC ? getZonedStartOfDay(addDays(relativeTo, -30)) : startOfDay(addDays(relativeTo, -30));
      endDate = toUTC ? getZonedEndOfDay(relativeTo) : endOfDay(relativeTo);
      break;
    case LAST_100_DAYS:
      startDate = toUTC ? getZonedStartOfDay(addDays(relativeTo, -100)) : startOfDay(addDays(relativeTo, -100));
      endDate = toUTC ? getZonedEndOfDay(relativeTo) : endOfDay(relativeTo);
      break;
    case THIS_MONTH:
      startDate = toUTC ? getZonedStartOfMonth(now) : startOfMonth(now);
      endDate = toUTC ? getZonedEndOfMonth(now) : endOfMonth(now);
      break;
    case LAST_MONTH:
      startDate = toUTC ? getZonedStartOfMonth(addMonths(now, -1)) : startOfMonth(addMonths(now, -1));
      endDate = toUTC ? getZonedEndOfMonth(addMonths(now, -1)) : endOfMonth(addMonths(now, -1));
      break;
    default:
      return {
        startDate: getUnixTime(daterange.start_date) + (toUTC ? getTimeZoneOffset() : 0),
        endDate: getUnixTime(daterange.end_date) + (toUTC ? getTimeZoneOffset() : 0),
      };
  }
  return {
    startDate: getUnixTime(startDate),
    endDate: getUnixTime(endDate),
  };
}

export function getInitialDate(queryParam) {
  if (queryParam) {
    let param;
    try {
      param = JSON.parse(queryParam);
    } catch {
      return null;
    }
    if (param.special) {
      if (![TODAY, LAST_7_DAYS, LAST_30_DAYS, THIS_MONTH, LAST_MONTH].includes(param.special)) {
        return null;
      }
      return param;
    } else {
      if (Number.isInteger(param.start_date) && Number.isInteger(param.end_date)) {
        if (param.start_date > param.end_date) {
          return null;
        }
      } else {
        return null;
      }
      return { start_date: fromUnixTime(param.start_date), end_date: fromUnixTime(param.end_date) };
    }
  }
  return null;
}

export function dateDistance(date) {
  return formatDistanceToNow(fromUnixTime(date), { includeSeconds: true, addSuffix: true });
}

export function getTimeZoneOffset() {
  return getTimezoneOffset(Intl.DateTimeFormat().resolvedOptions().timeZone) / 1000;
}

export function clampDateRange({ startDate, endDate }, { clampStart, clampEnd }) {
  let clampedStart = startDate;
  let clampedEnd = endDate;

  if (clampStart && isBefore(fromUnixTime(startDate), fromUnixTime(clampStart))) {
    clampedStart = clampStart;
  }
  if (clampEnd && isAfter(fromUnixTime(endDate), fromUnixTime(clampEnd))) {
    clampedEnd = clampEnd;
  }

  return { startDate: clampedStart, endDate: clampedEnd };
}
